// Procedural company emblems: motif × shape × colour scheme, rendered as inline SVG strings.
// Descriptor (stored in state.company.logo): { v: 1, motif, shape, scheme } — a few bytes in the save.
// Pure string building (no DOM), so it is safe to import anywhere in the UI.

export const MOTIFS = ['derrick', 'flame', 'drop', 'star', 'sunrise', 'pumpjack'];
export const SHAPES = ['shield', 'roundel', 'diamond', 'hex', 'plate'];
// [name, field, figure, rim]
export const SCHEMES = [
  ['Lamp oil', '#1b1611', '#f2a93b', '#c9a24a'],
  ['Oxblood', '#5b1d15', '#f3dfb8', '#c9a24a'],
  ['Forest', '#1f3a2a', '#f0d9a8', '#c9a24a'],
  ['Navy', '#1b2a45', '#ebc86e', '#d9d2c0'],
  ['Brass', '#c9a24a', '#1b1611', '#4a3210'],
  ['Ledger', '#efe4cf', '#a0441f', '#2a1f15'],
  ['Rust', '#b5532c', '#fff3dc', '#2a1f15'],
  ['Steel', '#394148', '#f2a93b', '#a3acb4'],
];

export const DEFAULT_LOGO = Object.freeze({ v: 1, motif: 'derrick', shape: 'shield', scheme: 0 });

export function normalizeLogo(d) {
  const o = d && typeof d === 'object' ? d : {};
  return {
    v: 1,
    motif: MOTIFS.includes(o.motif) ? o.motif : DEFAULT_LOGO.motif,
    shape: SHAPES.includes(o.shape) ? o.shape : DEFAULT_LOGO.shape,
    scheme: Number.isInteger(o.scheme) && o.scheme >= 0 && o.scheme < SCHEMES.length ? o.scheme : 0,
  };
}

export function randomLogo(rand = Math.random) {
  const pick = (a) => a[Math.floor(rand() * a.length)];
  return { v: 1, motif: pick(MOTIFS), shape: pick(SHAPES), scheme: Math.floor(rand() * SCHEMES.length) };
}

export const sameLogo = (a, b) => !!a && !!b && a.motif === b.motif && a.shape === b.shape && a.scheme === b.scheme;

// ---- geometry (viewBox 0 0 64 64) ----
const SHAPE_PATHS = {
  shield: { outer: 'M32 3.5 57 10v20.5C57 46 46.5 55.5 32 61 17.5 55.5 7 46 7 30.5V10Z', inner: 'M32 8.2 52.6 13.6v16.9C52.6 43.3 44 51.4 32 56.3 20 51.4 11.4 43.3 11.4 30.5V13.6Z', cy: 30 },
  roundel: { circle: true, cy: 32 },
  diamond: { outer: 'M32 2 62 32 32 62 2 32Z', inner: 'M32 7.6 56.4 32 32 56.4 7.6 32Z', cy: 32, scale: 0.86 },
  hex: { outer: 'M32 3 57.1 17.5v29L32 61 6.9 46.5v-29Z', inner: 'M32 7.8 53 19.9v24.2L32 56.2 11 44.1V19.9Z', cy: 32 },
  plate: { outer: 'M14 4h36l10 10v36L50 60H14L4 50V14Z', inner: 'M15.8 8.4h32.4l7.4 7.4v32.4l-7.4 7.4H15.8l-7.4-7.4V15.8Z', cy: 32 },
};

// Figures are drawn around (0,0) in a ~30×30 box, then placed at the shape's centre.
function figure(motif, fg, field) {
  switch (motif) {
    case 'derrick': return `<g fill="none" stroke="${fg}" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round">
        <path d="M0-14-8 13M0-14 8 13M-5.2 3.5h10.4M-3.4-3.8h6.8M-10.5 13h21"/>
        <path d="M-3.4-3.8 5.2 3.5M3.4-3.8-5.2 3.5M-5.2 3.5 6.6 9.2M5.2 3.5-6.6 9.2" stroke-width="1.15"/>
      </g><circle cx="0" cy="-14.5" r="2.1" fill="${fg}"/>`;
    case 'flame': return `<path d="M1.5-15C3 -7 11-4 11 4.5 11 11 6 15.5 0 15.5S-11 11-11 4.5c0-5.5 4-8.5 5-12.5 1.5 3 1.8 5.5 3.6 6.8C-1.2 -4.5-1-9 1.5-15Z" fill="${fg}"/>
      <path d="M.6 1.5C2 5 5.4 6.3 5.4 9.8 5.4 13 3 15 0 15S-5.4 13-5.4 9.8c0-2.4 1.8-3.6 2.4-5.6.9 1.4 1.3 2.4 2.2 3C-.6 5.3-.6 3.4.6 1.5Z" fill="${field}"/>`;
    case 'drop': return `<path d="M0-15.5C4.5-8 11-2.5 11 5A11 11 0 0 1-11 5C-11-2.5-4.5-8 0-15.5Z" fill="${fg}"/>
      <path d="M-6.2 4.2a6.4 6.4 0 0 0 4 6.2" fill="none" stroke="${field}" stroke-width="2" stroke-linecap="round" opacity=".85"/>`;
    case 'star': return `<path d="M0-15.5 4.2-4.8 15.5-4.3 6.7 2.8 9.6 13.7 0 7.6-9.6 13.7-6.7 2.8-15.5-4.3-4.2-4.8Z" fill="${fg}"/>
      <path d="M0-15.5 0 7.6 9.6 13.7 6.7 2.8 15.5-4.3 4.2-4.8Z" fill="${field}" opacity=".18"/>`;
    case 'sunrise': return `<g fill="${fg}"><path d="M-10 6a10 10 0 0 1 20 0Z"/>
        ${[-75, -45, -15, 15, 45, 75].map((a) => `<rect x="-1.3" y="-17" width="2.6" height="7" rx="1.3" transform="translate(0 6) rotate(${a})"/>`).join('')}
        <rect x="-15" y="8.5" width="30" height="2.6" rx="1.3"/><rect x="-10" y="13" width="20" height="2.4" rx="1.2"/></g>`;
    case 'pumpjack': return `<g fill="none" stroke="${fg}" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round">
        <path d="M-15 13.5h30M-4 13.5 0-1 4 13.5"/><path d="M-12-4.5 14-4.5" stroke-width="3"/>
        <path d="M-12-4.5c-3.4 0-4.4 4.5-1.6 7.2M-14.2 2.5v11M8-4.5 10 7"/></g>
      <circle cx="10" cy="8" r="3.6" fill="${fg}"/><circle cx="0" cy="-4.5" r="2.1" fill="${field}" stroke="${fg}" stroke-width="1.6"/>`;
    default: return '';
  }
}

let uid = 0;
// opts: { size = 64, title = '', ring = '' (text around a roundel, shown at size ≥ 72), className }
export function emblemSVG(desc, opts = {}) {
  const d = normalizeLogo(desc);
  const [, field, fg, rim] = SCHEMES[d.scheme];
  const size = opts.size ?? 64;
  const sh = SHAPE_PATHS[d.shape];
  const id = `em${++uid}`;
  const t = opts.title ? `<title>${String(opts.title).replace(/[<&]/g, '')}</title>` : '';
  let body;
  if (sh.circle) {
    const ring = opts.ring && size >= 72 ? String(opts.ring).toUpperCase().replace(/[<&]/g, '') : '';
    body = `<circle cx="32" cy="32" r="29.5" fill="${field}" stroke="${rim}" stroke-width="3"/>
      <circle cx="32" cy="32" r="${ring ? 19.5 : 24.5}" fill="none" stroke="${rim}" stroke-width="1" opacity=".75"/>
      ${ring ? `<path id="${id}r" d="M32 32m-23.6 0a23.6 23.6 0 1 1 47.2 0a23.6 23.6 0 1 1-47.2 0" fill="none"/>
        <text font-family="IBM Plex Sans Condensed, Arial Narrow, sans-serif" font-weight="600" font-size="5.6" letter-spacing="1.3" fill="${rim}"><textPath href="#${id}r" startOffset="0">${ring}</textPath></text>` : ''}`;
  } else {
    body = `<path d="${sh.outer}" fill="${field}" stroke="${rim}" stroke-width="2.6" stroke-linejoin="round"/>
      <path d="${sh.inner}" fill="none" stroke="${rim}" stroke-width="1" opacity=".7" stroke-linejoin="round"/>`;
  }
  const scale = (sh.scale ?? 1) * (sh.circle && opts.ring && size >= 72 ? 0.68 : 0.9);
  // subtle top sheen keeps flat colours from looking like clip art
  const sheen = `<linearGradient id="${id}g" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#fff" stop-opacity=".16"/><stop offset=".55" stop-color="#fff" stop-opacity="0"/><stop offset="1" stop-color="#000" stop-opacity=".18"/></linearGradient>`;
  const sheenShape = sh.circle ? `<circle cx="32" cy="32" r="28" fill="url(#${id}g)"/>` : `<path d="${sh.outer}" fill="url(#${id}g)"/>`;
  return `<svg class="${opts.className ?? 'emblem'}" viewBox="0 0 64 64" width="${size}" height="${size}" role="img" aria-label="${(opts.title ?? 'Company emblem').replace(/["<&]/g, '')}" xmlns="http://www.w3.org/2000/svg">${t}
    <defs>${sheen}</defs>${body}
    <g transform="translate(32 ${sh.cy}) scale(${scale})">${figure(d.motif, fg, field)}</g>${sheenShape}</svg>`;
}

// Initials for ring text: "Seneca Rock Oil Co." -> "S.R.O."
export function initials(name = '') {
  const skip = new Set(['co', 'co.', 'the', 'and', '&', 'of', 'company', 'inc', 'inc.']);
  const w = String(name).split(/\s+/).filter((x) => x && !skip.has(x.toLowerCase()));
  return w.slice(0, 4).map((x) => x[0].toUpperCase() + '.').join('');
}
